import { Component, computed, ElementRef, HostListener, inject, signal, ViewChild } from '@angular/core';
import { InternalAuthService, UtilityService } from '@eforall/common';
import { DroplistItem, DroplistWidget } from '@eforall/dashboard';
import { DatasetId } from '@interfaces';
import { AppDataService, FuncService, StaticDataService } from 'services';
import { AddDashboardDialogService } from '../add-dashboard-dialog/add-dashboard.dialog.service';
import { DashboardListPartService, LIST } from '../dashboard-list-part/dashboard-list.part.service';
import { getDroplistItems } from './get-droplist-items';


@Component({
	selector: 'home-toolbar-part',
	imports: [DroplistWidget],
	templateUrl: './home-toolbar.part.html',
	styleUrl: './home-toolbar.part.scss',
})
export class HomeToolbarPart {

	@ViewChild('menuBtn') menuBtn!: ElementRef;
	@ViewChild('menu') menu!: ElementRef;

	private auth = inject(InternalAuthService);
	private appData = inject(AppDataService);
	private dashboardListPartService = inject(DashboardListPartService);
	private staticData = inject(StaticDataService);
	private util = inject(UtilityService);
	private func = inject(FuncService);
	public readonly addDashboard = inject(AddDashboardDialogService);


	/**
	 * Whether the hamburger menu is currently visible.  The html uses an @if to
	 * display it when set.
	 */
	public isMenuOpen = signal(false);

	toggleMenu() {
		this.isMenuOpen.set(!this.isMenuOpen());
	}

	/**
	 * This function is called when the user selects a list view from either the dropdown or
	 * by clicking one in the hanburger menu.
	 * 
	 * @param datasetId - If the list == 'Dataset' then this will be the id of the selected one.
	 */
	public changeList(list: LIST, datasetId?: DatasetId) {

		//
		// If the list is 'Dataset' then the DataSetId will be passed in from onDroplistChange()
		// for which one we are filtering on.  We set that on the AppDataService to trigger the
		// specificDatasetDashboardItems computed to be filtered to just those that match.
		//
		if (list == 'Dataset') this.appData.specificDatasetFilter.set(datasetId!);

		//
		// Tell the DashboardListPart how to configure the grid.
		//
		this.dashboardListPartService.selectedList.set(list);

		//
		// Set the current droplist item
		//
		const selectedItem = this.droplistItems().find(i => (i.data !== 'Dataset' && i.data == list) || i.uniqueId == datasetId);
		if (!selectedItem) console.error('Droplist selectedItem was not found!');
		this._selectedDroplistItem.set(selectedItem!);

		//
		// Close the menu if it is open.
		//
		this.isMenuOpen.set(false);
	}


	/**
	 * Handles when the user clicks the Sign out menu option in the hanburger menu.
	 */
	async signOut() {
		await this.func.testBigQuery();
		//this.auth.signOut();
		this.isMenuOpen.set(false);
	}


	/**
	 * Listen for document clicks to determine if the hamburger menu is open
	 * and needs to be closed, because the user clicked outside the menu.
	 */
	@HostListener('document:click', ['$event'])
	documentClick(event: MouseEvent) {

		if (!this.isMenuOpen()) return;

		const clickedToggle = this.menuBtn.nativeElement.contains(event.target);
		const clickedInsideMenu = this.menu?.nativeElement.contains(event.target);

		if (!clickedToggle && !clickedInsideMenu) this.isMenuOpen.set(false);
	}



	//
	// Drop list code
	//

	/**
	 * This mediaQuery signal is used to hide the droplist on small screens.
	 */
	public readonly isSmall = this.util.mediaQuery.createSignal('(max-width: 652px)');

	/**
	 * The droplistItems currently never change but returned here as a computed signal for consistency.
	 */
	public readonly droplistItems = computed(() => { return getDroplistItems(this.staticData); });

	private _selectedDroplistItem = signal<DroplistItem<LIST, number>>(this.droplistItems()[2]!); // Initialized to Recent
	public selectedDroplistItem = this._selectedDroplistItem.asReadonly();

	public onDroplistChange(item: DroplistItem<LIST, number> | undefined) {
		if (item) this.changeList(item.data!, item.uniqueId);
	}

}
